// src/pages/Login/Login.jsx
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { toast } from "react-toastify";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { MdArrowBack } from "react-icons/md";

export default function Login() {
  const formRef = useRef();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    // 1) Kerätään lomaketiedot
    const formData = new FormData(formRef.current);
    const payload = {
      email: formData.get("email"),
      password: formData.get("password"),
    };

    try {
      // 2) Lähetetään kirjautumispyyntö palvelimelle
      const response = await axios.post(
        "https://spiderlegsproductions.com/api/login_user.php",
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      // 3) Tarkastetaan, että palvelin vastasi tokenilla
      if (response.data.success && response.data.token) {
        const token = response.data.token;

        // 4) Tallennetaan JWT localStorageen
        localStorage.setItem("token", token);

        let decoded = {};
        try {
          // 5) Dekoodataan tokenista payload
          decoded = jwtDecode(token);
        } catch (err) {
          console.error("Virhe tokenin dekoodauksessa:", err);
        }

        // 6) Tallennetaan käyttäjä-ID
        if (decoded.user_id) {
          localStorage.setItem("userId", decoded.user_id);
        }

        if (response.data.slug) {
            localStorage.setItem("performerSlug", response.data.slug);
          }
          
        // 7) Jos tokenissa on profiilityyppi, tallennetaan se ja ohjataan suoraan dashboardiin
        if (decoded.profile_type === "rental" || decoded.profile_type === "performer") {
          localStorage.setItem("profileType", decoded.profile_type);
          toast.success("Kirjautuminen onnistui! Ohjataan suoraan dashboardiin...");
          setTimeout(() => {
            if (decoded.profile_type === "rental") {
              navigate("/rental-dashboard");
            } else {
              navigate("/performer-dashboard");
            }
          }, 1000);
        } else {
          // 8) Muussa tapauksessa siirrytään profiilin valintasivulle
          toast.success("Kirjautuminen onnistui! Valitse profiili...");
          setTimeout(() => {
            navigate("/choose-profile");
          }, 1000);
        }
      } else {
        // Virhe kirjautumisessa
        toast.error(response.data.message || "Kirjautuminen epäonnistui.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Palvelinvirhe. Yritä hetken päästä uudelleen.");
    }
  };

  return (
    <div className="pt-24 pb-12 px-4 dark:bg-purple-950 text-darkest dark:text-primary dark:bg-opacity-75">
      <Helmet>
        <title>Spiderlegs Productions | Kirjaudu sisään</title>
        <meta
          name="description"
          content="Kirjaudu Spiderlegs-tilillesi ja hallitse varauksiasi tai esiintyjäprofiiliasi."
        />
      </Helmet>

      {/* Takaisin-painike */}
      <ResponsiveButton
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate("/");
          }
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        }}
        variant="backbutton"
        className="ml-4"
      >
        <div className="flex items-center gap-2">
          <MdArrowBack className="text-xl" />
          <span>Takaisin</span>
        </div>
      </ResponsiveButton>

        {/* Otsikko */}
        <h1 className="font-customFont2 text-center text-5xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-2 pt-8 dark:text-shadow-xxl">
        Kirjaudu sisään
        </h1>
        {/* Kuvaus */}
        <p className="text-center text-md sm:text-base text-darkest dark:text-primary dark:text-shadow-lg font-customFont1 mb-6 max-w-xl mx-auto">
        Kirjaudu sisään nähdäksesi vahvistetut laite- ja kärryvarauksesi.  
        Esiintyjäprofiilillasi voit muokata omia tietojasi, hallinnoida keikkojasi ja paljon muuta.
        </p>

      {/* Kirjautumislomake */}
      <motion.form
        ref={formRef}
        onSubmit={handleLogin}
        className="max-w-2xl mx-auto p-8 bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-xl space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Sähköposti */}
        <div>
          <label htmlFor="email" className="block font-customFont1 text-lg mb-2">
            Sähköposti *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            placeholder="Sähköpostiosoite..."
            className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
          />
        </div>

        {/* Salasana */}
        <div>
          <label htmlFor="password" className="block font-customFont1 text-lg mb-2">
            Salasana *
          </label>
          <input
            type="password"
            id="password"
            name="password"
            required
            placeholder="Salasana..."
            className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
          />
        </div>

        {/* Lähetä-painike */}
        <div className="flex justify-center">
          <ResponsiveButton type="submit" className="w-full max-w-xs rounded-full">
            Kirjaudu
          </ResponsiveButton>
        </div>

         {/* Rekisteröitymislinkki ja kuvaus */}
        <div className="mt-6 text-center text-md sm:text-base text-darkest dark:text-primary dark:text-shadow-lg max-w-xl mx-auto font-customFont1">
        <p className="mb-2">
            Eikö sinulla ole vielä tiliä? 
        </p>

        <a
            href="/register"
            className="inline-block font-semibold text-blue-600 dark:text-primary hover:text-white transition duration-150 underline hover:text-accent"
        >
            Luo tili
        </a>
        </div>
      </motion.form>
    </div>
  );
}
